import React from 'react';
import { ICms, IComponent } from '@/types/cms';
import dynamic from 'next/dynamic';
import { CmsComponentKeys, CmsLayoutKeys } from '../index';

/** 常用组件 */
import Banner from './Banner';
import ProductLine from './ProductLine';
import ProductList from './ProductList';
import FeaturedProduct from './featured-product';
import FeaturedHeader from './featured-header';
import FeaturedContent from './featured-content';
import Navigation from './Navigation';
import Title from './Title';
import Text from './Text';

const DynamicBannerCarousel = dynamic(() => import('@/components/component-pool/component-ssr/BannerCarousel'));
const DynamicBannerContainer = dynamic(() => import('@/components/component-pool/component-ssr/BannerContainer'));
const DynamicBannerLine = dynamic(() => import('@/components/component-pool/component-ssr/BannerLine'));
const DynamicProductArray = dynamic(() => import('@/components/component-pool/component-ssr/ProductArray'));
const DynamicProductSingle = dynamic(() => import('@/components/component-pool/component-ssr/ProductSingle'));
const DynamicProductWaterFall = dynamic(() => import('@/components/component-pool/component-ssr/ProductWaterfall'));
const DynamicButton = dynamic(() => import('@/components/component-pool/component-ssr/Button'));
const DynamicDivider = dynamic(() => import('@/components/component-pool/component-ssr/Divider'));
const DynamicBlock = dynamic(() => import('@/components/component-pool/component-ssr/Block'));
const DynamicVideo = dynamic(() => import('@/components/component-pool/component-ssr/Video'));
const DynamicTitleComplex = dynamic(() => import('@/components/component-pool/component-ssr/TitleComplex'));
const DynamicCoupon = dynamic(() => import('@/components/component-pool/component-ssr/Coupon'));
const DynamicCouponSingle = dynamic(() => import('@/components/component-pool/component-ssr/CouponSingle'));
const DynamicCountdown = dynamic(() => import('@/components/component-pool/component-ssr/Countdown'));
const DynamicPromotion = dynamic(() => import('@/components/component-pool/component-ssr/Promotion'));
const DynamicMarkdown = dynamic(() => import('@/components/component-pool/component-ssr/Markdown'));
const DynamicRaffleOrderList = dynamic(() => import('@/components/component-pool/component-ssr/RaffleOrderList'));
const DynamicRaffleAwardList = dynamic(() => import('@/components/component-pool/component-ssr/RaffleAwardList'));
const DynamicLayoutColumn = dynamic(() => import('@/components/component-pool/component-ssr/layout-column'));
const DynamicHeroVideo = dynamic(() => import('@/components/component-pool/component-ssr/hero-video'));
const DynamicHtml = dynamic(() => import('@/components/component-pool/component-ssr/CmsHtml'), { ssr: false });
const DynamicCmsJson = dynamic(() => import('@/components/component-pool/component-ssr/CmsJson'));
export interface CmsComponentRendererProps {
  component: IComponent;
  index: number;
  datasource: ICms['datasource'];
  trackingContext: { [key: string]: unknown };
  staticRes: { [key: string]: unknown };
  sections: ICms['layout']['sections'];
  top: number;
  isPreview?: boolean;
  uniqueVideoPlayerRef?: React.RefObject<null>;
  onDataSourceError?: () => void;
}

const CmsComponentRenderer: React.FC<CmsComponentRendererProps> = ({
  component,
  index,
  datasource,
  trackingContext,
  staticRes,
  sections,
  top,
  isPreview,
  uniqueVideoPlayerRef,
  onDataSourceError
}) => {
  const thisDataSource = component?.datasource?.[0];
  const staticResData = component.preview_data || staticRes[component.component_instance_key];

  return (
    <>
      {component.component_key === CmsComponentKeys.CM_ITEM_LINE && (
        <ProductLine
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
          isPreview={isPreview}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_ITEM_ARRAY && (
        <DynamicProductArray
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
          isPreview={isPreview}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_ITEM_LIST && (
        <ProductList
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
          isPreview={isPreview}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_PRODUCT && (
        <DynamicProductSingle
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
          isPreview={isPreview}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_ITEM_LIST_WATERFALL && (
        <DynamicProductWaterFall
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
          isPreview={isPreview}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_BANNER && (
        <Banner
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_CAROUSEL_BANNER && (
        <DynamicBannerCarousel
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_BANNER_CONTAINER && (
        <DynamicBannerContainer
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_BANNER_LINE && (
        <DynamicBannerLine
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_TITLE && (
        <Title
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_TEXT && (
        <Text
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_BUTTON && (
        <DynamicButton
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_DIVIDER && (
        <DynamicDivider
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_BLOCK && (
        <DynamicBlock
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_VIDEO && (
        <DynamicVideo
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          uniqueVideoPlayerRef={uniqueVideoPlayerRef}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_COMPLEX_TITLE && (
        <DynamicTitleComplex
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_COUPON && (
        <DynamicCoupon
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_SINGLE_COUPON && (
        <DynamicCouponSingle
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_COUNTDOWN && (
        <DynamicCountdown
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentDataSourceKey={thisDataSource}
          componentPosition={index + 1}
          fetchUrl={datasource[thisDataSource]?.now as string}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_NAVIGATION && (
        <Navigation
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          sections={sections}
          top={top}
          allStaticRes={staticRes}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_PROMOTION && (
        <DynamicPromotion
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          fetchUrl={datasource[thisDataSource]?.now as string}
          staticRes={staticResData}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_MARKDOWN && (
        <DynamicMarkdown
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_RAFFLE_ORDER && (
        <DynamicRaffleOrderList
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          fetchUrl={datasource[thisDataSource]?.now as string}
          staticRes={staticResData}
          top={top}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_RAFFLE_AWARD_LIST && (
        <DynamicRaffleAwardList
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          fetchUrl={datasource[thisDataSource]?.now as string}
          staticRes={staticResData}
          top={top}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_HERO_VIDEO && (
        <DynamicHeroVideo
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_FEATURED_HEADER && (
        <FeaturedHeader
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_FEATURED_CONTENT && (
        <FeaturedContent
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          top={top}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_FEATURED_PRODUCT && (
        <FeaturedProduct
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
          staticRes={staticResData}
          top={top}
          isPreview={isPreview}
          onDataSourceError={onDataSourceError}
        />
      )}
      {component.component_type === CmsLayoutKeys.LAYOUT_COLUMN && (
        <DynamicLayoutColumn
          sections={[
            {
              section_key: `layout_section_${component.component_instance_key}`,
              components: component.sub_components ?? []
            }
          ]}
          datasource={datasource}
          trackingContext={trackingContext}
          staticRes={staticRes}
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          properties={component.properties}
          top={top}
          isPreview={isPreview}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_HTML && (
        <DynamicHtml
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
        />
      )}
      {component.component_key === CmsComponentKeys.CM_JSON && (
        <DynamicCmsJson
          componentKey={component.component_key}
          componentInstanceKey={component.component_instance_key}
          componentPosition={index + 1}
          trackingContext={trackingContext}
          properties={component.properties}
        />
      )}
    </>
  );
};

export default CmsComponentRenderer;
